<template>
  <ul class="st" :class="{ 'st--compact': compact }" :aria-label="label">
    <li v-for="s in stats" :key="s.id" class="st-item" :data-test="`ti-stat-${s.id}`">
      <component
        :is="canOpen(s) ? 'button' : 'div'"
        class="st-box"
        :class="{ 'is-open': activeId !== null && activeId === s.opens, 'is-button': canOpen(s) }"
        :type="canOpen(s) ? 'button' : null"
        :aria-expanded="canOpen(s) ? (activeId === s.opens ? 'true' : 'false') : null"
        :title="compact ? `${s.label}: ${s.value} ${s.sub}` : null"
        @click="canOpen(s) && $emit('open', s.opens)"
      >
        <span class="st-label">{{ s.label }}</span>
        <b class="st-value">{{ s.value }}</b>
        <span v-if="!compact" class="st-sub">{{ s.sub }}</span>
        <span v-if="!compact && canOpen(s)" class="st-view">{{ activeId === s.opens ? 'Hide list' : 'View list' }}</span>
      </component>
    </li>
  </ul>
</template>

<script>
/**
 * The four numbers (follow-ups, scheduled or received, its value, the rate) on a team card and on each person's card (Team Insights
 * redesign). Presentational: chipRows.js buildStats makes the values from the classic arrays. A tile that has a list behind it
 * (`opens` names it) is a button that asks the card to open that list; `disabledIds` are lists with nothing in them.
 */
export default {
  name: 'StatTiles',
  props: {
    stats: { type: Array, default: () => [] },
    // the small version used on a person's card: label and number only
    compact: { type: Boolean, default: false },
    // tiles with a list behind them are buttons only when the card says so (a person's card has no per-person list)
    openable: { type: Boolean, default: false },
    activeId: { type: String, default: null },
    disabledIds: { type: Array, default: () => [] },
    label: { type: String, default: 'Totals' },
  },
  methods: {
    canOpen(s) {
      return this.openable && !!s.opens && this.disabledIds.indexOf(s.opens) === -1;
    },
  },
};
</script>

<style lang="scss" scoped>
@import '../../../components/Followups/mixins';

.st {
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
}

.st--compact {
  grid-template-columns: repeat(auto-fit, minmax(96px, 1fr));
  gap: 8px;
}

.st-item { min-width: 0; }

.st-box {
  @include fu-button-reset;
  @include fu-focus-ring;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 1px;
  width: 100%;
  height: 100%;
  padding: 12px 14px;
  text-align: left;
  border-radius: 18px;
  border: 1px solid $fu-border;
  background: $fu-surface-sunken;
  color: $fu-text;

  &.is-button { cursor: pointer; }
  &.is-button:hover { border-color: $fu-border-strong; }
  &.is-open { background: $fu-brand-50; border-color: $fu-brand-200; }
}

.st-label {
  font-size: 12.5px;
  color: $fu-text-muted;
}

.st-value {
  @include fu-tabular;
  font-size: 26px;
  font-weight: $fu-fw-semibold;
  letter-spacing: -0.02em;
  line-height: 1.15;
  color: $fu-text;
  overflow-wrap: anywhere;
}

.st-sub {
  font-size: 12px;
  color: $fu-text-strong;
}

.st-view {
  margin-top: 3px;
  font-size: 12px;
  font-weight: $fu-fw-semibold;
  color: $fu-brand-700;
}

.st--compact .st-box {
  padding: 8px 10px;
  border-radius: 14px;
}

.st--compact .st-label { font-size: 11.5px; line-height: 1.25; }
.st--compact .st-value { font-size: 17px; white-space: nowrap; }
</style>
